import type { MediaFormat } from '@/lib/mediaFormats';

type MediaSource = {
  src: string;
  width: number;
};

type MediaDimensionProps =
  | { width: number; height: number; aspectRatio?: never }
  | { aspectRatio: string; width?: never; height?: never };

export type MediaProps = MediaDimensionProps & {
  /** Responsive sources — widest descriptor used as fallback `src` (JPEG/PNG). */
  sources: ReadonlyArray<MediaSource> | string;
  /**
   * Optional next-gen `<source>` layers for `<picture>` (prefer AVIF, then WebP).
   * Use `modernMediaFormats(sources)` when sibling `.avif`/`.webp` files exist.
   */
  formats?: ReadonlyArray<MediaFormat>;
  sizes: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  className?: string;
};

function buildSrcSet(sources: ReadonlyArray<MediaSource> | string): { src: string; srcSet: string } {
  if (typeof sources === 'string') {
    return { src: sources, srcSet: sources };
  }

  const sorted = [...sources].sort((a, b) => a.width - b.width);
  const fallback = sorted[sorted.length - 1];

  return {
    src: fallback.src,
    srcSet: sorted.map(({ src, width }) => `${src} ${width}w`).join(', '),
  };
}

/**
 * Responsive image with `srcset`/`sizes` and reserved layout space (explicit
 * dimensions or `aspect-ratio`) to prevent CLS. Replaces banned `next/image`.
 * When `formats` is set, renders `<picture>` with AVIF/WebP sources and a
 * JPEG/PNG fallback `<img>`.
 *
 * @example
 * <Media
 *   sources={[
 *     { src: '/media/hero-640.jpg', width: 640 },
 *     { src: '/media/hero-1280.jpg', width: 1280 },
 *   ]}
 *   formats={modernMediaFormats([
 *     { src: '/media/hero-640.jpg', width: 640 },
 *     { src: '/media/hero-1280.jpg', width: 1280 },
 *   ])}
 *   sizes="(min-width: 768px) 50vw, 100vw"
 *   alt="Arctic tern in flight"
 *   width={1280}
 *   height={720}
 * />
 */
export function Media({
  sources,
  formats,
  sizes,
  alt,
  loading = 'lazy',
  decoding = 'async',
  className,
  ...dimensionProps
}: MediaProps) {
  const { src, srcSet } = buildSrcSet(sources);

  const style =
    'aspectRatio' in dimensionProps && dimensionProps.aspectRatio
      ? { aspectRatio: dimensionProps.aspectRatio }
      : undefined;

  const width = 'width' in dimensionProps ? dimensionProps.width : undefined;
  const height = 'height' in dimensionProps ? dimensionProps.height : undefined;

  const img = (
    // eslint-disable-next-line @next/next/no-img-element -- ADR-004: custom Media replaces next/image
    <img
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      loading={loading}
      decoding={decoding}
      className={formats && formats.length > 0 ? undefined : className}
      width={width}
      height={height}
      style={style}
    />
  );

  if (!formats || formats.length === 0) {
    return img;
  }

  return (
    <picture className={className}>
      {formats.map((format) => {
        const { srcSet: formatSrcSet } = buildSrcSet(format.sources);
        return (
          <source key={format.type} type={format.type} srcSet={formatSrcSet} sizes={sizes} />
        );
      })}
      {img}
    </picture>
  );
}
